<template>
  <parent>
    <loading-page v-if="fetchingAccountBuckets && fetchingExternalAccounts" />
    <div v-else>
      <validation-observer
        ref="originRules"
        v-slot="{ invalid, errors }"
        tag="div"
        :class="{ blurred: noDestinations && !externalAccounts.length > 0 }"
      >
        <b-row class="justify-content-center">
          <b-col
            cols="12"
            md="10"
          >
            <paper title="Create Job">
              <div class="px-2 pb-1">
                <b-row>
                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Input"
                      rules="required"
                    >
                      <input-field
                        name="create_job-input"
                        label="Input*"
                        label-position="top"
                        :error="errors.length ? errors[0] : null"
                      >
                        <b-form-input
                          id="create_job-input"
                          v-model="jobModel.filePath"
                          placeholder="Enter input file URL..."
                          :state="errors.length > 0 ? false : null"
                        />
                      </input-field>
                    </validation-provider>
                  </b-col>
                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <input-field
                      name="create_job-destination"
                      label="Destination*"
                      label-position="top"
                      class="mr-35 mw-100"
                      :error="errors.Destination ? errors.Destination[0] : errors.bucket ? errors.bucket[0] : 'No Bucket & Destinations'"
                    >
                      <b-input-group class="mw-100">
                        <transition name="fade">
                          <div v-if="!noDestinations || externalAccounts.length > 0">
                            <validation-provider
                              #default="{ errors }"
                              name="bucket"
                              rules="required"
                              tag="div"
                              class="w-100"
                              immediate
                            >
                              <b-dropdown
                                id="object-options"
                                :disabled="fetchingExternalAccounts && fetchingAccountBuckets"
                                variant="bg-white"
                                offset="20"
                                no-caret
                                :state="errors.length > 0 ? false : null"
                                @show="isOpenExternalAccount = true"
                                @hide="isOpenExternalAccount = false"
                              >
                                <template
                                  #button-content
                                >
                                  <div v-if="selectedAccount">
                                    <img
                                      :src="selectedAccount.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : selectedAccount.providerImageUrl"
                                      width="30px"
                                      height="30px"
                                      alt="Storage External Account"
                                      class="mr-1"
                                    >
                                    <span><b>{{ selectedAccount.parentId === '0' ? 'Captain' : selectedAccount.username }}</b></span>
                                    <GjIcon
                                      class="ml-1"
                                      :class="isOpenExternalAccount ? 'arrow' : 'arrow-down'"
                                      name="ArrowDown"
                                    />
                                  </div>
                                </template>
                                <ul
                                  v-for="(account, property) in externalAccounts"
                                  :key="property"
                                  class="list-unstyled m-0 p-0"
                                >
                                  <b-dropdown-item @click="changeSelectedAccount(account)">
                                    <img
                                      :src="account.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : account.providerImageUrl"
                                      width="30px"
                                      height="30px"
                                      alt=""
                                      class="mr-1"
                                    >
                                    <span class="main"><strong>{{ account.parentId === '0' ? 'Captain ' : account.username }}</strong></span>
                                  </b-dropdown-item>
                                </ul>
                              </b-dropdown>
                            </validation-provider>
                          </div>
                        </transition>
                        <transition name="fade">
                          <div v-if="!noDestinations || externalAccounts.length > 0">
                            <validation-provider
                              #default="{ errors }"
                              name="bucket"
                              rules="required"
                              tag="div"
                              class="w-100"
                              immediate
                            >
                              <custom-select
                                id="job-bucket"
                                v-model="selectedBucket"
                                class="bucket-input"
                                label="name"
                                :loading="fetchingAccountBuckets || fetchingExternalAccounts"
                                :disabled="!!selectedAccount.bucketName"
                                :state="errors.length > 0 ? false:null"
                                :options="getBucketOptions(accountBuckets)"
                              />
                            </validation-provider>
                          </div>
                        </transition>
                        <b-input-group-prepend class="flex-fill mw-100">
                          <span class="destination-split">/</span>

                          <validation-provider
                            #default="{ errors }"
                            name="Destination"
                            rules="validateForwardSlash"
                            tag="div"
                            class="w-100"
                          >
                            <b-form-input
                              v-model="jobModel.destination"
                              placeholder="Enter file destination..."
                              :state="errors.length > 0 ? false : null"
                              style="min-width: 150px;"
                            />
                          </validation-provider>
                        </b-input-group-prepend>
                      </b-input-group>
                    </input-field>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Callback URL"
                    >
                      <input-field
                        name="create_job-destination"
                        label="Callback URL"
                        label-position="top"
                        info-position="label"
                        :error="errors.length ? errors[0] : null"
                        :info="{
                          title: 'Callback Response JSON',
                          content: callbackResponse,
                        }"
                        @openInfo="viewJson"
                      >
                        <b-form-input
                          id="create_job-destination"
                          v-model="jobModel.callbackUrl"
                          placeholder="Enter callback URL..."
                          :state="errors.length > 0 ? false : null"
                        />
                      </input-field>
                    </validation-provider>
                  </b-col>
                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <input-field
                      name="create_job-template"
                      label="Job template*"
                      label-position="top"
                    >
                      <div class="jobTemplateName align-items-center d-flex">
                        <span v-if="selectedJobTemplate">
                          {{ selectedJobTemplate.name }}
                        </span>
                        <div
                          v-else
                          class="text-muted italic"
                        >
                          Select a job template from below
                        </div>
                      </div>
                    </input-field>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-form-group
                      label="Generate Thumbnail"
                      class="text-regular text-secondary field-label checkboxcreate"
                    >
                      <b-form-checkbox
                        v-model="jobModel.generateThumbnail"
                        class="toggle-spacing"
                        switch
                      />
                    </b-form-group>
                  </b-col>
                  <b-col>
                    <input-field
                      name="tags"
                      label="Tags"
                      label-position="top"
                    >
                      <tags
                        full-width
                        :tags="jobModel.jobTags"
                        @remove-field="onRemoveField"
                      />
                    </input-field>
                  </b-col>
                </b-row>
              </div>
            </paper>
          </b-col>
        </b-row>
        <b-row
          id="jobTemplates"
          class="justify-content-center"
        >
          <b-col
            cols="12"
            md="10"
          >
            <paper title="Job Template">
              <template v-slot:header-right>
                <b-button
                  variant="outline-primary"
                  class="btn-paper-header"
                  :to="{ name: 'job-templates-new', query: { from: 'jobs' } }"
                >
                  New Job Template
                </b-button>
              </template>
              <job-templates
                :selected="selectedJobTemplate"
                @selected="setJobTemplate"
                @create="
                  $router.push({
                    name: 'job-templates-new',
                    query: { from: 'jobs' },
                  })
                "
              />
            </paper>
          </b-col>
        </b-row>
        <b-row class="justify-content-center">
          <b-col
            cols="12"
            md="10"
          >
            <div
              class="d-flex justify-content-end py-2 border-default border-t mt-1"
            >
              <b-button
                variant="bg-white"
                :to="{ name: 'jobs' }"
              >
                Cancel
              </b-button>
              <button-loading
                variant="bg-primary"
                class="ml-1"
                :disabled="invalid || !isJobTemplateSelected || (accountBuckets.length <= 0 && !selectedAccount.bucketName)"
                :spinning="creatingJob"
                @click="createJob"
              >
                Create
              </button-loading>
            </div>
          </b-col>
        </b-row>
        <json-preview v-model="jsonToPreview" />
      </validation-observer>
      <pop-up
        v-if="noDestinations && !externalAccounts.length > 0"
        class="position-absolute bg-transparent"
        title="Create an External Account"
        button-text="Integrate Storage"
        :show-cancel="false"
        go-back
        @add="$router.push({ name: 'add-external-account' })"
        @goBack="$router.push({ name: 'jobs' })"
      >
        To be able to create a Job you need to have an External Account with a Bucket.
      </pop-up>
    </div>
  </parent>
</template>

<script>
import InputField from '@/components/form/InputField.vue'
import Paper from '@/components/paper/Paper.vue'
import Tags from '@/components/tags/Tags.vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import JsonPreview from '@/components/info/JsonPreview.vue'
import JobTemplates from './JobTemplates.vue'
import { callbackResponse } from './code'
import PopUp from '@/components/popup/Pop-up.vue'

export default {
  name: 'CreateJob',
  components: {
    Paper,
    InputField,
    JobTemplates,
    ValidationProvider,
    ValidationObserver,
    JsonPreview,
    Tags,
    PopUp,
  },
  props: {
    clone: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      callbackResponse,
      required,
      selectedJobTemplate: null,
      jsonToPreview: null,
      isOpen: false,
      isOpenExternalAccount: false,
      noDestinations: false,
    }
  },
  computed: {
    isJobTemplateSelected() {
      return this.selectedJobTemplate
    },
    externalAccounts() {
      return this.$store.state.externalAccount.externalAccounts
    },
    fetchingExternalAccounts() {
      return this.$store.state.externalAccount.fetchingExternalAccounts
    },
    creatingJob() {
      return this.$store.state.jobs.creatingJob
    },
    fetchingBuckets() {
      return this.$store.state.buckets.fetchingBuckets
    },
    selectedAccount: {
      get() {
        return this.$store.state.externalAccount.externalAccount || null
      },
      set(account) {
        this.$store.commit('externalAccount/CHANGE_ACCOUNT', account)
      },
    },
    selectedBucket: {
      get() {
        return this.$store.state.externalAccount.selectedBucket || null
      },
      set(bucketName) {
        this.$store.commit('externalAccount/CHANGE_BUCKET_NAME', bucketName)
      },
    },
    fetchingAccountBuckets() {
      return this.$store.state.accountBuckets.fetchingBuckets
    },
    buckets() {
      return this.$store.state.buckets.buckets
    },
    lastIndex() {
      return this.jobModel.jobTags.length
        ? this.jobModel.jobTags[this.jobModel.jobTags.length - 1].index
        : 0
    },
    jobModel() {
      return this.$store.state.jobs.jobModel
    },
    accountBuckets: {
      get() {
        return this.$store.state.accountBuckets.buckets
      },
      set(account) {
        const arr = [{ ...account }]
        this.$store.commit('accountBuckets/UPDATE_BUCKETS', arr)
      },
    },
    accountId: {
      get() {
        return this.$store.state.externalAccount.accountId
      },
      set(val) {
        this.$store.commit('externalAccount/SET_ACCOUNT_ID', val)
      },
    },
  },
  created() {
    this.getAccounts()
    if (this.$store.state.app.previousRouteName !== 'job-templates-new') {
      this.$store.commit('jobs/SET_JOB_MODEL')
    }
    if (!this.jobModel.initialized) {
      const url = this.clone?.destination.replace(
        'http://blob.gjirafa.com/',
        '',
      )
      const index = url?.indexOf('/')
      if (this.clone) {
        this.jobModel.gjBlob = this.checkBlob(url.slice(0, index))
        this.jobModel.destination = url.slice(index + 1, url.length)
        this.jobModel.filePath = this.clone.input.filePath
        this.jobModel.callbackUrl = this.clone.input.callbackUrl
        this.selectedJobTemplate = {
          id: this.clone.jobTemplateId,
          name: this.clone.jobTemplateName,
        }
        this.clone.tags.forEach(element => {
          this.jobModel.jobTags.push({
            index: this.lastIndex + 1,
            key: element.key,
            value: element.value,
          })
        })
      }
    }
    if (this.$route.params.toast) {
      const { toast } = this.$route.params
      this.successToast(toast.message)
    }
  },
  methods: {
    setJobTemplate(e) {
      if (!this.selectedJobTemplate || e.id !== this.selectedJobTemplate.id) {
        this.selectedJobTemplate = e
      } else {
        this.selectedJobTemplate = null
      }
    },
    createJob() {
      let isNull = true
      let tags = []
      this.jobModel.jobTags.forEach(element => {
        if (element.key !== '' && element.value !== '') {
          isNull = false
          tags.push({
            key: element.key,
            value: element.value,
          })
        }
      })
      if (isNull) {
        tags = []
      }

      if (this.accountBuckets.length <= 0 && !this.selectedAccount.bucketName) {
        this.errorToast('Please specify a bucket!')
        return
      }

      const query = {
        input: {
          filePath: this.jobModel.filePath,
          callbackUrl: this.jobModel.callbackUrl,
        },
        destination: `${!this.noDestinations ? `${this.selectedBucket}/` : ''}${this.jobModel.destination}`,
        jobTemplateId: this.selectedJobTemplate.id,
        generateThumbnail: this.jobModel.generateThumbnail,
        s3AccountId: this.accountId,
        tags,
      }

      this.$store
        .dispatch('jobs/createJob', query)
        .then(() => {
          this.$router.push({
            name: 'jobs',
            params: {
              projectId: this.$route.params.projectId,
              toast: {
                title: 'Success',
                variant: 'success',
                message: 'Job created successfully!',
              },
            },
          })
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0] || 'An error occurred.',
          )
        })
    },
    getAccounts() {
      this.$store
        .dispatch('externalAccount/fetchAccounts')
        .then(() => {
          this.changeSelectedAccount(this.selectedAccount)
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors
              || 'Something went wrong while fetching all external accounts.',
          )
        })
    },
    changeSelectedBucket(bucket) {
      this.selectedBucket = bucket
    },
    changeSelectedAccount(account) {
      if (account === null) return null
      this.selectedAccount = account
      this.accountId = account?.id || 0
      const bucketName = this.selectedAccount?.bucketName

      // eslint-disable-next-line eqeqeq
      if (!bucketName || this.selectedAccount.parentId == 0) {
        this.getBuckets()
      } else {
        this.accountBuckets = { name: bucketName }
        this.selectedBucket = bucketName
        this.$store.commit('accountBuckets/CANCEL_FETCHING_BUCKETS')
      }

      return this.selectedAccount
    },
    viewJson(a) {
      this.jsonToPreview = a
    },
    onRemoveField(index) {
      this.jobModel.jobTags = this.jobModel.jobTags.filter(
        obj => obj.index !== index,
      )
    },
    checkBlob(name) {
      const bucket = this.buckets.find(b => b.name === name)
      return bucket?.name || null
    },
    getBuckets(url, index) {
      this.$store
        .dispatch('accountBuckets/fetchBuckets', this.accountId)
        .then(() => {
          this.selectedBucket = this.isNullOrEmpty(this.selectedAccount?.bucketName)
            ? this.accountBuckets[0]?.name
            : this.selectedAccount.bucketName

          // TODO: fix this, this will throw if url and index are null
          if (this.clone) this.jobModel.gjBlob = this.checkBlob(url.slice(0, index))
          else this.jobModel.gjBlob = this.selectedBucket
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 424) {
            if (!this.externalAccounts.length > 0) {
              this.noDestinations = true
              this.$store.commit('accountBuckets/SET_SHOW_INTRO', true)
              this.$store.commit('buckets/SET_SHOW_INTRO', true)
              return
            }
          }
          if (status === 426 || status === 428) {
            this.$store.commit('accountBuckets/SET_SERVICE_UNAVAILABLE', status === 428)
            this.$store.commit('buckets/SET_SERVICE_UNAVAILABLE', status === 428)
            return
          }
          if (status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching buckets.',
          )
        })
    },
    getBucketOptions: options => options.map(o => ({ text: o.name, value: o.name })),
  },
}
</script>

<style lang="scss" scoped>
.jobTemplateName {
  height: 42px;
}
.form-control:focus,
.custom-select:focus,
.form-control:focus-within,
.input-group:focus-within .form-control {
  border-color: #e0e5eb !important;
}
.destination-split {
  display: flex;
  height: 100%;
  padding: 0 15px;
  align-items: center;
  border-left: 1px solid #e0e5eb;
  border-top: 1px solid #e0e5eb;
  border-bottom: 1px solid #e0e5eb;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}

.custom-width::v-deep{
  width: 150px;
  max-width: 150px;

  .dropdown-menu {
    li{
      &:last-child{
        padding-bottom: 10px;
      }
    }

    max-height: 220px;
    overflow-y: auto;
  }
}

.custom-width-input{
  width: 100px;
}
.blurred {
  filter: blur(5px);
  -webkit-filter: blur(5px);
}
.checkboxcreate {
  width: 150px;
}
.arrow {
    transform: rotate(180deg);
    transition: 0.2s ease;
}

.bucket-input::v-deep{
  min-width: 200px;
  .dropdown-menu {
    height: auto;
    max-height: 200px;
    overflow-y: auto;
  }
}

.arrow-down {
    transform: rotate(0);
    transition: 0.2s ease;
}
</style>
